import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { createTenantLoader } from "@/lib/tenantLoad.mjs";

const TenantContext = createContext(null);

const DEFAULT_BRANDING = {
  primary_color: "#e85d1f",
  secondary_color: "#13141a",
  accent_color: "#f59e0b",
  tagline: "Operational Field Coordination Infrastructure",
  powered_by_forgedops: true,
  footer_text: "Powered by ForgedOps™",
  logo_url: null,
};

function applyBranding(branding) {
  const root = document.documentElement;
  if (branding?.primary_color) {
    root.style.setProperty("--fop-primary", branding.primary_color);
    // soft variant
    const hex = branding.primary_color.replace("#", "");
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      root.style.setProperty("--fop-primary-soft", `rgba(${r}, ${g}, ${b}, 0.12)`);
    }
  }
  if (branding?.accent_color) {
    root.style.setProperty("--fop-accent", branding.accent_color);
  }
  if (branding?.secondary_color) {
    root.style.setProperty("--fop-secondary", branding.secondary_color);
  }
}

export function TenantProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const identity = authLoading ? "checking" : user ? user.id + ":" + user.tenant_id : "public";
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const loader = useRef(null);
  if (!loader.current) loader.current = createTenantLoader(api);
  const [state, setState] = useState({ identity: null, tenant: null, error: null, loading: true });

  const reload = useCallback(async () => {
    if (authLoading || identityRef.current !== identity) return;
    const expected = identity;
    setState({ identity: expected, tenant: null, error: null, loading: true });
    applyBranding(DEFAULT_BRANDING);
    await loader.current.load(user, () => identityRef.current === expected,
      (data) => {
        setState({ identity: expected, tenant: data, error: null, loading: false });
        applyBranding({ ...DEFAULT_BRANDING, ...data.branding });
      },
      (failure) => {
        const detail = failure?.response?.data?.detail;
        const error = typeof detail === "string" ? detail : "Unable to load your company settings. Please retry.";
        setState({ identity: expected, tenant: user ? null : { branding: DEFAULT_BRANDING, display_name: "ForgedOps Plans" }, error, loading: false });
      });
  }, [authLoading, identity, user]);

  useEffect(() => {
    reload();
    return () => loader.current.invalidate();
  }, [reload]);

  const tenant = state.identity === identity ? state.tenant : null;
  const loading = authLoading || state.identity !== identity || state.loading;
  const error = state.identity === identity ? state.error : null;

  const update = async (payload) => {
    const expected = identity;
    const { data } = await api.patch("/tenant", payload);
    if (data.id !== user?.tenant_id) throw new Error("Company identity mismatch.");
    if (identityRef.current !== expected) return data;
    loader.current.invalidate();
    setState({ identity, tenant: data, error: null, loading: false });
    applyBranding({ ...DEFAULT_BRANDING, ...data.branding });
    return data;
  };

  return (
    <TenantContext.Provider value={{ tenant, loading, error, update, reload }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
